/**
 * Video reviews (sections/video-reviews.liquid)
 *
 * The theme's Swiper runs the videos, centred and looping; a click on a side
 * video brings it to the middle. As on live, only the centre video plays,
 * muted, and the others pause. The card's toggle pauses and resumes it, and
 * the speaker button turns its sound on, muting any other; a side video's
 * speaker brings it to the middle with its sound on. With reduced motion
 * nothing plays until the toggle is pressed.
 *
 * Docs: docs/sections/video-reviews.md
 * @extends HTMLElement
 */
class VideoReviews extends HTMLElement {
  swiper = null;
  // The slide index whose speaker was pressed while it sat at the side
  unmuteIndex = null;
  autoplay = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  constructor() {
    super();
    this.onWindowLoad = this.onWindowLoad.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onVideoState = this.onVideoState.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    // `play` and `pause` do not bubble, so they are caught on the way down.
    this.addEventListener('play', this.onVideoState, true);
    this.addEventListener('pause', this.onVideoState, true);

    // theme.liquid loads Swiper with `defer`; fall back to the load event if it has not run yet.
    if (typeof Swiper === 'undefined') {
      window.addEventListener('load', this.onWindowLoad, { once: true });
      return;
    }

    this.initSwiper();
  }

  disconnectedCallback() {
    window.removeEventListener('load', this.onWindowLoad);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('play', this.onVideoState, true);
    this.removeEventListener('pause', this.onVideoState, true);
    this.swiper?.destroy(true, true);
    this.swiper = null;
  }

  get carousel() {
    return this.querySelector('.video-reviews__carousel');
  }

  onWindowLoad() {
    this.initSwiper();
  }

  initSwiper() {
    if (this.swiper || typeof Swiper === 'undefined' || !this.carousel) return;

    const slideCount = this.carousel.querySelectorAll('.swiper-slide').length;

    this.swiper = new Swiper(this.carousel, {
      slidesPerView: 'auto',
      centeredSlides: true,
      loop: slideCount > 1,
      loopedSlides: slideCount,
      slideToClickedSlide: true,
      watchOverflow: true,
      speed: this.autoplay ? 400 : 0,
      navigation: {
        prevEl: this.querySelector('[data-video-reviews-prev]'),
        nextEl: this.querySelector('[data-video-reviews-next]'),
      },
      // Swiper's a11y module sets the buttons' aria-label from these; its own defaults are hardcoded English.
      a11y: {
        prevSlideMessage: this.dataset.previousLabel,
        nextSlideMessage: this.dataset.nextLabel,
      },
      on: {
        afterInit: () => this.playActive(),
        slideChangeTransitionEnd: () => this.playActive(),
      },
    });
  }

  /**
   * Loads the three videos on screen, plays the centre one (the loop may have made it a copy) and pauses the rest.
   */
  playActive() {
    const activeSlide = this.carousel.querySelector('.swiper-slide-active');
    const active = activeSlide?.querySelector('video');
    const unmute = this.unmuteIndex === activeSlide?.dataset.swiperSlideIndex;
    this.unmuteIndex = null;

    for (const video of this.carousel.querySelectorAll(':is(.swiper-slide-active, .swiper-slide-prev, .swiper-slide-next) video')) {
      if (video.preload !== 'none') continue;

      video.preload = 'metadata';
      video.load();
    }

    for (const video of this.carousel.querySelectorAll('video')) {
      if (video === active) continue;

      video.pause();
      video.muted = true;
      this.syncButtons(video);
    }

    if (active && unmute) {
      active.muted = false;
      this.syncButtons(active);
    }

    // play() rejects if a pause interrupts it or the browser blocks it; neither is an error here.
    if (active && (this.autoplay || unmute)) active.play().catch(() => {});
  }

  /**
   * @param {MouseEvent} event
   */
  onClick(event) {
    const soundButton = event.target.closest('[data-video-reviews-sound]');
    const playButton = event.target.closest('[data-video-reviews-play]');
    const video = (soundButton || playButton)?.closest('.video-reviews__card')?.querySelector('video');
    if (!video) return;

    // A side video's speaker: Swiper brings its slide to the middle, and playActive turns its sound on there.
    if (!video.closest('.swiper-slide-active')) {
      if (soundButton) this.unmuteIndex = video.closest('.swiper-slide')?.dataset.swiperSlideIndex ?? null;
      return;
    }

    if (soundButton) {
      const unmute = video.muted;

      for (const other of this.carousel.querySelectorAll('video')) {
        other.muted = true;
        this.syncButtons(other);
      }

      video.muted = !unmute;
      if (unmute && video.paused) video.play().catch(() => {});
      this.syncButtons(video);
      return;
    }

    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }

  /**
   * Keeps a card's buttons on its video's state, whatever changed it.
   *
   * @param {Event} event
   */
  onVideoState(event) {
    if (event.target instanceof HTMLVideoElement) this.syncButtons(event.target);
  }

  /**
   * @param {HTMLVideoElement} video
   */
  syncButtons(video) {
    const card = video.closest('.video-reviews__card');
    card?.querySelector('[data-video-reviews-play]')?.setAttribute('aria-pressed', String(!video.paused));
    card?.querySelector('[data-video-reviews-sound]')?.setAttribute('aria-pressed', String(!video.muted));
  }
}

if (!customElements.get('video-reviews')) {
  customElements.define('video-reviews', VideoReviews);
}
